/* eslint-disable no-unused-vars */
import { createContext, useEffect, useReducer } from "react";
// utils
import { isValidToken, setSession } from "utils/jwt";
import { LoginApiResponseTypes } from "features/validations";
// types
import type { FC, PropsWithChildren } from "react";
import { ILoginResponse, IUser } from "../types/auth.types";
import { useProfileQuery } from "services";
import { paths } from "routes/paths";

// ----------------------------------------------------------------------

interface IAuthState {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: IUser | null;
}

const initialState: IAuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state: any, action: any) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state: any, action: any) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state: any) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state: any, action: any) =>
  // @ts-ignore
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  method: "jwt",
  login: (data: LoginApiResponseTypes) => Promise.resolve(),
  logout: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const token = localStorage.getItem("token");
  const user = localStorage.getItem("user");
  const {
    data,
    isLoading: isProfileLoading,
    isSuccess: isProfileSuccess,
  } = useProfileQuery(
    {},
    {
      skip: !token,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
    }
  );
  const profileUser = data?.user;

  useEffect(() => {
    const initialize = () => {
      if (token && isValidToken(token) && user) {
        setSession(token);
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: true,
            isInitialized: true,
            user:
              isProfileSuccess && profileUser ? profileUser : JSON.parse(user),
          },
        });
      } else {
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, [data]);

  function login(data: ILoginResponse) {
    const { user, session } = data;
    localStorage.setItem("token", session.token);
    localStorage.setItem("user", JSON.stringify(user));

    dispatch({
      type: "LOGIN",
      payload: {
        user,
      },
    });
    dispatch({
      type: "INITIALIZE",
      payload: {
        isAuthenticated: true,
        isInitialized: true,
        user: user,
      },
    });
    window.location.replace(paths.root);
  }

  function logout() {
    setSession(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.replace(paths.auth.login);

    dispatch({ type: "LOGOUT" });
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
